import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { Calendar, Users, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SchedulePage = () => {
  const { t, language, isRTL } = useLanguage();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const response = await axios.get(`${API}/schedule`);
        setSchedules(response.data);
      } catch (error) {
        console.error('Error fetching schedule:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'ar-SA', {
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="w-12 h-12 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#FDFCF8] min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} max-w-3xl`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {t.schedule.subtitle}
            </p>
            <h1
              data-testid="schedule-title"
              className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-6xl font-medium text-[#1A1A1A] mb-6`}
            >
              {t.schedule.title}
            </h1>
            <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg text-[#666660] leading-relaxed`}>
              {language === 'en'
                ? 'Reserve your spot in our upcoming training sessions. Limited seats available for each class.'
                : 'احجزي مقعدك في جلساتنا التدريبية القادمة. مقاعد محدودة متاحة لكل دورة.'}
            </p>
          </div>
        </div>
      </section>

      {/* Schedule Grid */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          {schedules.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-[#C5A059]/50 mx-auto mb-6" />
              <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg text-[#666660]`}>
                {t.schedule.noSchedule}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {schedules.map((schedule, index) => (
                <Card
                  key={schedule.id}
                  data-testid={`schedule-card-${index}`}
                  className="group relative overflow-hidden bg-white border border-[#E6E2D6]/50 hover:border-[#C5A059]/30 transition-all duration-500 hover:shadow-2xl hover:shadow-[#C5A059]/5"
                >
                  <CardContent className={`p-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {/* Date */}
                    <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-12 h-12 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="font-manrope text-sm text-[#666660]">
                          {formatDate(schedule.start_date)} – {formatDate(schedule.end_date)}
                        </p>
                      </div>
                    </div>

                    {/* Course Title */}
                    <h3 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-2xl font-medium text-[#1A1A1A] mb-4`}>
                      {language === 'en' ? schedule.course_title_en : schedule.course_title_ar}
                    </h3>

                    {/* Spots */}
                    <div className={`flex items-center gap-2 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Users className="w-4 h-4 text-[#C5A059]" />
                      <p className="font-manrope text-sm text-[#666660]">
                        <span className="font-medium text-[#C5A059]">{schedule.spots_available}</span>{' '}
                        {t.schedule.spotsAvailable}
                      </p>
                    </div>

                    {/* CTA */}
                    <Link to="/contact">
                      <Button
                        data-testid={`reserve-spot-${index}`}
                        className="w-full rounded-full bg-[#C5A059] text-white hover:bg-[#B08A3E] py-6 text-base font-medium shadow-[0_4px_14px_0_rgba(197,160,89,0.39)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.23)] hover:-translate-y-1 transition-all duration-300"
                      >
                        {t.schedule.reserveSpot}
                        <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SchedulePage;
